import { Injectable, computed, inject, signal } from '@angular/core';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthSessionService } from '@/core/services/auth-session.service';
import { ClientChatStatsApiService } from './client-chat-stats-api.service';
import { MarketFeathersService } from './market-feathers.service';
import { SupportChatService, SupportChatStats } from './support-chat.service';
import { ClientChatStatsItem, UserChatPreview } from '@/core/models/client-chat-stats';

const EMPTY_SUPPORT_STATS: SupportChatStats = {
  unread: 0,
  lastMessage: null,
  lastMessageAt: null,
};

function userPreviewKey(clientId: number, userId: number, distributorId?: number | null): string {
  if (distributorId != null && !Number.isNaN(Number(distributorId))) {
    return `${clientId}:${userId}:${Number(distributorId)}`;
  }
  return `${clientId}:${userId}`;
}

@Injectable({ providedIn: 'root' })
export class ClientChatStatsStore {
  private readonly api = inject(ClientChatStatsApiService);
  private readonly feathers = inject(MarketFeathersService);
  private readonly authSession = inject(AuthSessionService);
  private readonly supportChat = inject(SupportChatService);

  private readonly clientsUnreadMap = signal<Record<number, number>>({});
  private readonly usersPreviewMap = signal<Record<string, UserChatPreview>>({});
  private readonly conversations = signal<ClientChatStatsItem[]>([]);
  private readonly supportStats = signal<SupportChatStats>(EMPTY_SUPPORT_STATS);
  private readonly revision = signal(0);

  private realtimeBound = false;
  private readonly inflightClientIds = new Set<number>();
  private supportStatsInflight = false;

  readonly revision$ = computed(() => this.revision());

  totalUnread(): number {
    this.revision();
    const clients = Object.values(this.clientsUnreadMap()).reduce((sum, value) => sum + (Number(value) || 0), 0);
    return clients + (Number(this.supportStats().unread) || 0);
  }

  supportPreview(): SupportChatStats {
    this.revision();
    return this.supportStats();
  }

  conversationItems(): ClientChatStatsItem[] {
    this.revision();
    return this.conversations();
  }

  loadConversations(): void {
    this.ensureAuth().subscribe({
      next: () => {
        this.api.find({ $limit: 50, $skip: 0 }).subscribe({
          next: (response) => {
            const data = response.data || [];
            this.conversations.set(data);
            for (const item of data) {
              this.applyClientItem(item);
            }
            this.revision.update((v) => v + 1);
          },
        });
        this.refreshSupport();
      },
    });
  }

  constructor() {
    this.ensureRealtime();
  }

  clientUnread(clientId: number): number {
    this.revision();
    return this.clientsUnreadMap()[clientId] ?? 0;
  }

  userPreview(clientId: number, userId: number, distributorId?: number | null): UserChatPreview | null {
    this.revision();
    const map = this.usersPreviewMap();
    if (distributorId != null) {
      const keyed = map[userPreviewKey(clientId, userId, distributorId)];
      if (keyed) {
        return keyed;
      }
    }
    return map[userPreviewKey(clientId, userId)] ?? null;
  }

  hydrateClients(
    clients: Array<number | { clientId: number; distributorId?: number | null }>
  ): void {
    const seen = new Set<number>();
    for (const entry of clients) {
      const clientId = typeof entry === 'number' ? entry : Number(entry?.clientId);
      const distributorId =
        typeof entry === 'number' ? undefined : entry?.distributorId != null ? Number(entry.distributorId) : undefined;

      if (!Number.isFinite(clientId) || clientId <= 0 || seen.has(clientId)) {
        continue;
      }
      seen.add(clientId);
      this.refreshClient(clientId, Number.isFinite(distributorId as number) ? (distributorId as number) : undefined);
    }
  }

  refreshSupport(): void {
    if (this.supportStatsInflight) {
      return;
    }

    this.ensureAuth().subscribe({
      next: (user) => {
        this.supportStatsInflight = true;
        const currentUserId = Number(user?.id ?? this.authSession.session()?.user?.id) || null;
        this.supportChat.getStats(currentUserId).subscribe({
          next: (stats) => {
            this.supportStatsInflight = false;
            this.supportStats.set(stats);
            this.revision.update((v) => v + 1);
          },
          error: () => {
            this.supportStatsInflight = false;
          },
        });
      },
    });
  }

  refreshClient(clientId: number, distributorId?: number): void {
    if (!clientId || this.inflightClientIds.has(clientId)) {
      return;
    }

    this.ensureAuth().subscribe({
      next: () => {
        this.inflightClientIds.add(clientId);
        this.api.findByClientId(clientId, distributorId).subscribe({
          next: (item) => {
            this.inflightClientIds.delete(clientId);
            if (item) {
              this.applyClientItem(item);
            }
          },
          error: () => {
            this.inflightClientIds.delete(clientId);
          },
        });
      },
    });
  }

  private applyClientItem(item: ClientChatStatsItem): void {
    const clientId = Number(item.clientId);
    this.clientsUnreadMap.update((state) => ({
      ...state,
      [clientId]: Number(item.totalUnread) || 0,
    }));

    const nextUsers: Record<string, UserChatPreview> = { ...this.usersPreviewMap() };
    for (const key of Object.keys(nextUsers)) {
      if (key === String(clientId) || key.startsWith(`${clientId}:`)) {
        delete nextUsers[key];
      }
    }

    for (const user of item.users || []) {
      const preview: UserChatPreview = {
        unread: Number(user.unread) || 0,
        lastMessage: user.lastMessage ?? null,
        lastMessageImageUrl: user.lastMessageImageUrl ?? null,
        lastMessageAt: user.lastMessageAt ?? null,
        distributorId: user.distributorId ?? null,
      };
      nextUsers[userPreviewKey(clientId, user.id)] = preview;
      if (user.distributorId != null) {
        nextUsers[userPreviewKey(clientId, user.id, user.distributorId)] = preview;
      }
    }

    this.usersPreviewMap.set(nextUsers);
    this.revision.update((v) => v + 1);
  }

  private ensureAuth() {
    const token = this.authSession.getToken();
    const current = this.feathers.currentUserSubject.value;
    if (current?.id) {
      return of(current);
    }
    if (!token) {
      return this.feathers.isAuthChat().pipe(take(1));
    }
    return this.feathers.authenticate(token);
  }

  private ensureRealtime(): void {
    if (this.realtimeBound) {
      return;
    }
    this.realtimeBound = true;

    const messages = this.feathers.service('messages/:type/:typeId');
    messages.on('created', (msg: any) => this.onMessageCreated(msg));
    messages.on('patched', (msg: any) => this.onMessagePatched(msg));

    const usersChat = this.feathers.service('users-chat');
    usersChat.on('created', (msg: any) => this.onSupportMessageChanged(msg));
    usersChat.on('patched', (msg: any) => this.onSupportMessageChanged(msg));
    usersChat.on('removed', (msg: any) => this.onSupportMessageChanged(msg));
  }

  private onMessageCreated(msg: any): void {
    if (!msg || msg.type !== 'client-connect') {
      return;
    }
    const clientId = Number(msg.typeId);
    if (!clientId) {
      return;
    }
    const distributorId = msg.distributorId != null ? Number(msg.distributorId) : undefined;
    this.refreshClient(clientId, distributorId);
  }

  private onMessagePatched(msg: any): void {
    if (!msg) {
      return;
    }
    // After mark-read, backend emits patched with readByUserId for the reader only.
    if (msg.readByUserId != null || msg.readAt != null) {
      const clientId = Number(msg.typeId);
      if (msg.type === 'client-connect' && clientId) {
        const distributorId = msg.distributorId != null ? Number(msg.distributorId) : undefined;
        this.refreshClient(clientId, distributorId);
      }
    }
  }

  private onSupportMessageChanged(msg: any): void {
    if (String(msg?.type || '') === 'support') {
      this.refreshSupport();
    }
  }
}
